"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAuth } from "@/components/providers/auth-provider";
import { fetcher } from "@/lib/api";
import { formatLocalPrice } from "@/lib/currency";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import type { LearnHubTrack, LearnTrackCard, LearnViewer } from "@/features/learn/api";
import { bandMessageKey } from "@/features/learn/skill-test/api";
import { LearnModuleStrip } from "./LearnModuleStrip";
import { LearnTrackThumb, trackMark } from "./LearnTrackThumb";

/**
 * The hub's tracks - `LN-HUB01`, docs/35 §4, redrawn in `LN-UI04`.
 *
 * **It renders the server's payload on the first paint and never waits for a
 * session.** `tracks` comes from the ISR-cached read in
 * `app/[locale]/learn/page.tsx`, so the cards are in the server HTML for a
 * crawler and for a browser with no JavaScript; `web-ssr-was-a-skeleton` is the
 * trap this avoids by construction.
 *
 * The viewer half (progress, the Continue row, the Skill Score band) is one
 * authenticated read of the same route after hydration, and only when there is
 * a session to read it with. One request for the whole grid, not one per card.
 *
 * **Two shapes, by count** (`LN-UI04`, founder's review of 2026-09-23). One
 * track is a single wide card, because a lone card in a three-column grid is a
 * page that looks empty. Two or more are a grid whose last cell says more
 * tracks are coming; no language is named there, because the founder did not
 * want a list of promises on the page.
 */
type ViewerByTrack = Record<string, LearnViewer | null>;

/** What the hub's server read adds per track: its modules, and where lesson 1 is. */
export type LearnTrackExtras = Record<
  string,
  { modules: boolean[]; firstLessonHref: string | null }
>;

async function fetchViewerTracks(): Promise<ViewerByTrack> {
  const res = await fetcher<{ tracks: LearnHubTrack[] }>("/v1/learn/tracks");
  if (!res.success) return {};
  const out: ViewerByTrack = {};
  for (const row of res.data.tracks ?? []) out[row.trackSlug] = row.viewer ?? null;
  return out;
}

export function LearnTracksGrid({
  tracks,
  extras,
  locale,
}: {
  tracks: LearnTrackCard[];
  extras: LearnTrackExtras;
  locale: Locale;
}) {
  const t = useTranslations("learn");
  const { isAuthenticated } = useAuth();

  const { data: viewers } = useQuery({
    queryKey: ["learn", "tracks", "viewer"],
    queryFn: fetchViewerTracks,
    enabled: isAuthenticated && tracks.length > 0,
    staleTime: 60_000,
  });

  const continueRow = tracks
    .map((track) => ({ track, viewer: viewers?.[track.trackSlug] ?? null }))
    .filter((row) => row.viewer && row.viewer.progress.completedLessons > 0);

  return (
    <>
      {continueRow.length > 0 ? (
        <section aria-labelledby="learn-continue" className="mb-14">
          <h2
            className="font-[family-name:var(--font-heading)] text-xl font-bold text-[rgb(var(--foreground))]"
            id="learn-continue"
          >
            {t("continueHeading")}
          </h2>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {continueRow.map(({ track, viewer }) => {
              const percent = viewer?.progress.percent ?? 0;
              return (
                <Card className="flex items-center gap-4 rounded-[var(--radius)] p-4 sm:p-5" key={track.trackSlug}>
                  <span
                    aria-hidden
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius)] bg-primary font-[family-name:var(--font-heading)] text-sm font-bold text-white"
                  >
                    {trackMark(track)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-[family-name:var(--font-heading)] font-bold text-[rgb(var(--foreground))]">
                      {locale === "bn" && track.title_bn ? track.title_bn : track.title_en}
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <ProgressBar className="w-full max-w-56" percent={percent} />
                      <span className="shrink-0 text-xs font-semibold text-[rgb(var(--foreground))]">
                        {t("progressLabel", { percent: formatNumber(percent, locale) })}
                      </span>
                    </div>
                  </div>
                  <Button asChild className="shrink-0">
                    <Link href={`/${locale}/learn/${track.trackSlug}`}>
                      {t("continueCta")}
                      <ArrowRight aria-hidden className="h-4 w-4" />
                    </Link>
                  </Button>
                </Card>
              );
            })}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="learn-tracks">
        <h2
          className="scroll-mt-24 font-[family-name:var(--font-heading)] text-2xl font-bold tracking-tight text-[rgb(var(--foreground))] lg:text-3xl"
          id="learn-tracks"
        >
          {t("tracksHeading")}
        </h2>
        <p className="mt-2 text-sm text-neutral-600 lg:text-base">{t("tracksSubtitle")}</p>

        {tracks.length === 1 ? (
          <>
            <FeaturedTrackCard
              extras={extras[tracks[0].trackSlug]}
              locale={locale}
              track={tracks[0]}
              viewer={viewers?.[tracks[0].trackSlug] ?? null}
            />
            <div className="mt-5 flex items-center gap-4 rounded-[var(--radius)] border border-dashed border-neutral-300 bg-neutral-50 px-5 py-4">
              <MoreTracksMark className="h-10 w-10" />
              <p className="text-sm text-neutral-600">
                <strong className="block font-[family-name:var(--font-heading)] text-[15px] text-[rgb(var(--foreground))]">
                  {t("moreTracksTitle")}
                </strong>
                {t("moreTracksBody")}
              </p>
            </div>
          </>
        ) : (
          <div className="mt-7 grid gap-6 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4">
            {tracks.map((track) => (
              <TrackCard
                extras={extras[track.trackSlug]}
                key={track.trackSlug}
                locale={locale}
                track={track}
                viewer={viewers?.[track.trackSlug] ?? null}
              />
            ))}
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-[var(--radius)] border-[1.5px] border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
              <MoreTracksMark className="h-14 w-14" />
              <p className="mt-1 font-[family-name:var(--font-heading)] text-lg font-bold text-neutral-700">
                {t("moreTracksTitle")}
              </p>
              <p className="max-w-64 text-sm leading-relaxed text-neutral-600">{t("moreTracksBody")}</p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

function MoreTracksMark({ className }: { className: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border border-dashed border-neutral-400 text-neutral-500",
        className,
      )}
    >
      <Plus className="h-5 w-5" />
    </span>
  );
}

function ProgressBar({ percent, className }: { percent: number; className?: string }) {
  return (
    <span aria-hidden className={cn("block h-1.5 overflow-hidden rounded-full bg-neutral-100", className)}>
      <span className="block h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
    </span>
  );
}

function usePriceLabel(track: LearnTrackCard, locale: Locale): string {
  const t = useTranslations("learn");
  return track.planCount > 0 && track.fromPrice !== null && track.fromPrice > 0
    ? t("priceFrom", { price: formatLocalPrice(track.fromPrice, "BDT", locale) })
    : t("priceFree");
}

function ViewerProgress({ viewer, locale }: { viewer: LearnViewer | null; locale: Locale }) {
  const t = useTranslations("learn");
  if (!viewer || viewer.progress.totalLessons === 0) return null;
  // `LN-TST01`: the viewer's best Skill Test band, printed by its name.
  const bandKey = bandMessageKey(viewer.skillBand);
  const percent = viewer.progress.percent;
  return (
    <div className="flex items-center gap-2.5 text-xs text-neutral-600">
      <span className="hidden sm:inline">{t("yourProgress")}</span>
      <ProgressBar className="w-20" percent={percent} />
      <span className="font-semibold text-[rgb(var(--foreground))]">
        {formatNumber(percent, locale)}%{bandKey ? ` · ${t(bandKey)}` : ""}
      </span>
    </div>
  );
}

function FeaturedTrackCard({
  track,
  extras,
  locale,
  viewer,
}: {
  track: LearnTrackCard;
  extras: LearnTrackExtras[string] | undefined;
  locale: Locale;
  viewer: LearnViewer | null;
}) {
  const t = useTranslations("learn");
  const title = locale === "bn" && track.title_bn ? track.title_bn : track.title_en;
  const tagline = locale === "bn" && track.tagline_bn ? track.tagline_bn : track.tagline_en;
  const modules = extras?.modules ?? [];
  const out = modules.filter(Boolean).length;
  const counts = track.lessonAccessCounts;
  const n = (value: number) => formatNumber(value, locale);
  const stats: Array<{ label: string; value: ReactNode }> = [
    { label: t("statLessons"), value: n(track.lessonCount) },
    { label: t("statOpen"), value: n(counts.free + counts.core) },
    { label: t("statPro"), value: n(counts.pro) },
  ];
  if (modules.length > 0) {
    stats.push({
      label: t("statModulesOut"),
      value: (
        <>
          {n(out)}{" "}
          <span className="text-[15px] font-medium text-neutral-500">
            {t("statOfTotal", { total: n(modules.length) })}
          </span>
        </>
      ),
    });
  }
  const priceLabel = usePriceLabel(track, locale);

  return (
    <Card className="mt-7 grid overflow-hidden rounded-[var(--radius)] lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
      <div className="flex items-center bg-neutral-950">
        <LearnTrackThumb
          mark={trackMark(track)}
          thumbnailImageId={track.thumbnailImageId}
          variant="hero"
        />
      </div>
      <div className="flex flex-col p-6 lg:px-9 lg:py-8">
        <div className="flex flex-wrap gap-2">
          <StatusBadge label={priceLabel} tone="success" />
          <StatusBadge label={t("chipFromZero")} tone="neutral" />
        </div>
        <h3 className="mt-4 font-[family-name:var(--font-heading)] text-2xl font-bold tracking-tight text-[rgb(var(--foreground))] lg:text-[1.75rem]">
          {title}
        </h3>
        {tagline ? (
          <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-neutral-600">{tagline}</p>
        ) : null}

        <dl className="mt-6 grid grid-cols-2 gap-x-4 border-y border-neutral-100 sm:grid-cols-4 sm:gap-x-0 sm:divide-x sm:divide-neutral-100">
          {stats.map((stat) => (
            <div className="py-3.5 sm:px-5 sm:first:pl-0" key={stat.label}>
              <dt className="text-xs text-neutral-500">{stat.label}</dt>
              <dd className="mt-1 font-[family-name:var(--font-heading)] text-[22px] font-bold text-[rgb(var(--foreground))]">
                {stat.value}
              </dd>
            </div>
          ))}
        </dl>

        {modules.length > 0 ? (
          <div className="mt-4">
            <LearnModuleStrip
              label={t("countModulesOut", { out: formatNumber(out, locale), total: formatNumber(modules.length, locale) })}
              modules={modules}
            />
            <p className="mt-2 text-[13px] text-neutral-600">
              {t("modulesOutLine", { out: n(out), total: n(modules.length) })}
            </p>
          </div>
        ) : null}

        <div className="mt-auto flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <Button asChild className="h-11" variant="secondary">
              <Link href={`/${locale}/learn/${track.trackSlug}`}>
                {t("openTrack")}
                <ArrowRight aria-hidden className="h-4 w-4" />
              </Link>
            </Button>
            {extras?.firstLessonHref ? (
              <Link
                className="text-sm font-medium text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-primary-ink hover:decoration-primary"
                href={`/${locale}${extras.firstLessonHref}`}
              >
                {t("readFirstLesson")}
              </Link>
            ) : null}
          </div>
          <ViewerProgress locale={locale} viewer={viewer} />
        </div>
      </div>
    </Card>
  );
}

function TrackCard({
  track,
  extras,
  locale,
  viewer,
}: {
  track: LearnTrackCard;
  extras: LearnTrackExtras[string] | undefined;
  locale: Locale;
  viewer: LearnViewer | null;
}) {
  const t = useTranslations("learn");
  const title = locale === "bn" && track.title_bn ? track.title_bn : track.title_en;
  const tagline = locale === "bn" && track.tagline_bn ? track.tagline_bn : track.tagline_en;
  const modules = extras?.modules ?? [];
  const out = modules.filter(Boolean).length;
  const priceLabel = usePriceLabel(track, locale);

  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-[var(--radius)] transition hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]">
      <LearnTrackThumb mark={trackMark(track)} thumbnailImageId={track.thumbnailImageId} />
      <div className="flex flex-1 flex-col p-5 lg:p-6">
        <div>
          <StatusBadge label={priceLabel} tone="success" />
        </div>
        <h3 className="mt-3 font-[family-name:var(--font-heading)] text-xl font-bold text-[rgb(var(--foreground))]">
          {title}
        </h3>
        {tagline ? (
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-neutral-600">{tagline}</p>
        ) : null}
        <p className="mt-4 text-[13px] text-neutral-600">
          {t("countLessons", { count: formatNumber(track.lessonCount, locale) })}
          {modules.length > 0 ? ` · ${t("countModulesOut", { out: formatNumber(out, locale), total: formatNumber(modules.length, locale) })}` : ""}
        </p>
        {modules.length > 0 ? (
          <LearnModuleStrip
            className="mt-2"
            label={t("countModulesOut", { out: formatNumber(out, locale), total: formatNumber(modules.length, locale) })}
            modules={modules}
          />
        ) : null}
        <div className="mt-auto flex items-center justify-between gap-3 pt-5">
          <Link
            className="inline-flex items-center gap-1.5 font-[family-name:var(--font-heading)] text-[15px] font-bold text-primary-ink hover:underline"
            href={`/${locale}/learn/${track.trackSlug}`}
          >
            {t("openTrack")}
            <ArrowRight aria-hidden className="h-4 w-4" />
          </Link>
          <ViewerProgress locale={locale} viewer={viewer} />
        </div>
      </div>
    </Card>
  );
}
